/* global YT */
import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../config/firebaseConfig";
import AudioPlayer from "./AudioPlayer";
import MultipleChoiceQuestion from "./MultipleChoiceQuestion";
import MultipleCheckboxQuestion from "./MultipleCheckboxQuestion";
import SentenceOrdering from "./SentenceOrdering";
import FillInTheBlanks from "./FillInTheBlanks";
import SpellTheWord from "./SpellTheWord";
import Pairing from "./Pairing";
import "./ModuleDetailPage.css";
import "./StudentModuleView.css";
import { UserContext } from "../../../../App";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  addToFeedbackQueue,
  generateModuleSummaryFeedback,
} from "./AITextFeedback";
import { useUnsavedChanges } from "../../../../contexts/UnsavedChangesContext";
import QuestionWrapper from "./helpers/QuestionWrapper";
import AudioRecorderWrapper from "./helpers/AudioRecorderWrapper";
import ModuleOverallFeedback from "./ModuleOverallFeedback";
import AvailabilityCountdown from "../AvailabilityCountdown";
import TheGuessinator from "./games/TheGuessinator";
import Flashcards from "./games/Flashcards";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faSave,
  faCheck,
  faPaperPlane,
  faEye,
  faTrash,
  faRetweet,
} from "@fortawesome/free-solid-svg-icons";
import { FaClock } from "react-icons/fa";
import OpenTextQuestion from "./OpenTextQuestion";
import { isCountableQuestion } from "./helpers/materialHelpers";

const StudentModuleView = ({ setPageTitle }) => {
  const { courseId, sectionId, moduleId, userId } = useParams();
  const location = useLocation();
  const [moduleData, setModuleData] = useState(null);
  const [courseName, setCourseName] = useState(
    location.state?.courseName || ""
  );
  const [lastEditDate, setLastEditDate] = useState(null);
  const [studentResponses, setStudentResponses] = useState({});
  const [reRecordingIndex, setReRecordingIndex] = useState(null);
  const { uid, avatar, firstName, lastName, role } = useContext(UserContext);
  const [aiFeedback, setAiFeedback] = useState({});
  const [practiceResults, setPracticeResults] = useState({});
  const [isCheckAnswersLoading, setIsCheckAnswersLoading] = useState(false);
  const [isSubmitAnswersLoading, setIsSubmitAnswersLoading] = useState(false);
  const [sectionAvailability, setSectionAvailability] = useState(null);
  const navigate = useNavigate();
  const studentResponsesRef = useRef({});
  const [editingFeedback, setEditingFeedback] = useState(null);
  const { setUnsavedChanges, handleProtectedNavigation } = useUnsavedChanges();
  const isTeacher = [
    "teacher",
    "teacherplus",
    "admin",
    "technician",
    "technicianplus",
  ].includes(role);
  const isViewingAsStudent = userId && userId !== uid;
  const [isAutoEvaluating, setIsAutoEvaluating] = useState(false);
  const [error, setError] = useState(null);
  const [modifiedFeedback, setModifiedFeedback] = useState({});
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [moduleOverallAiFeedback, setModuleOverallAiFeedback] = useState(null);
  const [hasChangedSinceSubmission, setHasChangedSinceSubmission] =
    useState(false);

  // Add flip functionality for students
  const [flippedCards, setFlippedCards] = useState(new Set());
  const materialRefs = useRef([]);

  useEffect(() => {
    let apiLoaded = false;

    const loadYouTubeAPI = () => {
      if (
        !apiLoaded &&
        !document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        )
      ) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        apiLoaded = true;
      }
    };

    loadYouTubeAPI();
  }, []);

  useEffect(() => {
    setPageTitle("Module View");
    const fetchModuleData = async () => {
      try {
        const getModuleDataFunction = httpsCallable(functions, "getModuleData");
        const result = await getModuleDataFunction({
          courseId,
          sectionId,
          moduleId,
          studentId: userId || uid,
        });
        setModuleData({
          ...result.data.moduleData,
          isQuizMode: result.data.moduleData.isQuizMode || false,
          AIChatEnabled: result.data.moduleData.AIChatEnabled || false,
        });
        setSectionAvailability(result.data.sectionAvailability);
        if (!courseName) {
          setCourseName(result.data.courseName);
        }
        setLastEditDate(() => {
          if (!result.data.lastSubmissionDate) return null;

          // If it's a Firestore Timestamp
          if (result.data.lastSubmissionDate._seconds) {
            return new Date(result.data.lastSubmissionDate._seconds * 1000);
          }

          // If it's an ISO string
          const date = new Date(result.data.lastSubmissionDate);
          return isNaN(date.getTime()) ? null : date;
        });
        if (result.data.studentResponses) {
          setStudentResponses(result.data.studentResponses);
          studentResponsesRef.current = result.data.studentResponses;
        }
      } catch (error) {
        console.error("Error fetching module data:", error);
        setError(
          "This module is not open. Please contact your teacher to change the availability settings."
        );
      }
    };

    fetchModuleData();
  }, [courseId, sectionId, moduleId, setPageTitle, courseName, uid, userId]);

  // Track unsaved changes for both students and teachers
  useEffect(() => {
    let hasUnsavedChanges = false;

    if (isTeacher) {
      // For teachers: Check if there's unsaved feedback
      hasUnsavedChanges = Object.keys(modifiedFeedback).length > 0;
    } else {
      // For students: Check for unsaved changes in two scenarios:
      // 1. They have responses but haven't submitted yet (no lastEditDate)
      // 2. They have submitted but made changes since then (hasChangedSinceSubmission)
      hasUnsavedChanges =
        (Object.keys(studentResponses).length > 0 && !lastEditDate) ||
        hasChangedSinceSubmission;
    }

    setUnsavedChanges(hasUnsavedChanges);

    // Cleanup when component unmounts
    return () => {
      setUnsavedChanges(false);
    };
  }, [
    studentResponses,
    modifiedFeedback,
    isTeacher,
    setUnsavedChanges,
    lastEditDate,
    hasChangedSinceSubmission,
  ]);

  // Prevent browser navigation when there are unsaved changes
  useEffect(() => {
    const checkUnsavedChanges = () => {
      if (isTeacher) {
        return Object.keys(modifiedFeedback).length > 0;
      } else {
        return (
          (Object.keys(studentResponses).length > 0 && !lastEditDate) ||
          hasChangedSinceSubmission
        );
      }
    };

    const handleBeforeUnload = (e) => {
      if (checkUnsavedChanges()) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes that will be lost. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    // Add beforeunload listener for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    studentResponses,
    modifiedFeedback,
    isTeacher,
    lastEditDate,
    hasChangedSinceSubmission,
  ]);

  useEffect(() => {
    studentResponsesRef.current = studentResponses;
  }, [studentResponses]);

  useEffect(() => {
    const initializePlayers = () => {
      moduleData?.materials.forEach((material, index) => {
        if (material.type === "video" && material.youtubeLink) {
          new YT.Player(`youtube-player-${index}`, {
            height: "360",
            width: "640",
            videoId: new URL(material.youtubeLink).searchParams.get("v"),
          });
        }
      });
    };

    if (typeof YT !== "undefined" && YT.Player) {
      initializePlayers();
    } else {
      window.onYouTubeIframeAPIReady = initializePlayers;
    }
  }, [moduleData]);

  const handleModuleClick = () => {
    if (!isTeacher) return;
    handleProtectedNavigation(() =>
      navigate(`/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`)
    );
  };

  const handleCourseClick = () => {
    if (!isTeacher) return;
    handleProtectedNavigation(() => navigate(`/courses/${courseId}`));
  };

  const handleGoBackToModule = () => {
    handleProtectedNavigation(() =>
      navigate(`/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`)
    );
  };

  const handleDeleteSubmission = async () => {
    const confirmMessage = isViewingAsStudent
      ? "Are you sure you want to delete this student's submission for this module? This action cannot be undone."
      : "Are you sure you want to delete your submission for this module? This action cannot be undone.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleteLoading(true);
    try {
      const deleteSubmissionFunction = httpsCallable(
        functions,
        "deleteStudentSubmission"
      );
      await deleteSubmissionFunction({
        courseId,
        moduleId,
        studentId: userId || uid, // Pass the student ID (for teachers viewing students) or current user ID
      });

      // Clear local state
      setStudentResponses({});
      setLastEditDate(null);
      setHasChangedSinceSubmission(false); // Reset flag after deletion
      studentResponsesRef.current = {};

      alert("Submission deleted successfully!");
    } catch (error) {
      console.error("Error deleting submission:", error);
      alert("Failed to delete submission. Please try again.");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (error) {
    return (
      <div className="loading-message module-unavailable">
        <FaClock className="unavailable-icon" />
        <div className="unavailable-text">
          <p>This module is not open yet, please contact your teacher.</p>
          <p className="settings-hint">
            Teachers can adjust this in the course availability settings.
          </p>
        </div>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className="loading-message">Module contents are loading...</div>
    );
  }

  const handleStudentResponse = (questionId, response) => {
    // Mark as changed since submission when any response is modified
    if (lastEditDate) {
      setHasChangedSinceSubmission(true);
    }

    if (response.type === "Pairing") {
      const pairsArray = Object.entries(response.answer || {}).map(
        ([left, right]) => ({
          left,
          right: right === null ? "Not paired" : right,
        })
      );

      setStudentResponses((prev) => {
        const updatedResponses = {
          ...prev,
          [questionId]: {
            type: response.type,
            answer: pairsArray,
          },
        };
        studentResponsesRef.current = updatedResponses;
        return updatedResponses;
      });
    } else {
      setStudentResponses((prev) => {
        const updatedResponses = {
          ...prev,
          [questionId]: {
            type: response.type,
            answer: response.answer,
          },
        };
        studentResponsesRef.current = updatedResponses;
        return updatedResponses;
      });
    }
  };

  const getQuestionData = (question) => {
    switch (question.questionType) {
      case "Multiple Choice":
      case "Multiple Checkbox":
        return {
          question: question.question,
          options: question.options,
          correctAnswers: question.correctAnswers,
        };
      case "Fill in the Blanks":
        return {
          question: question.question,
          correctAnswers: question.correctAnswers,
        };
      case "Pairing":
        return {
          question: question.question,
          pairs: question.pairs,
          options: question.options,
          leftItems: question.leftItems,
          rightItems: question.rightItems,
          questionCategories: question.questionCategories || [],
        };
      case "Sentence Ordering":
        return {
          fullSentence: question.question,
          correctAnswers: question.correctAnswers,
        };
      case "Spell the Word":
        return {
          correctSpelling: question.sentence.match(/\[.*?\]/g)[0].slice(1, -1),
          sentence: question.sentence.replace(/\[.*?\]/g, "___"),
        };
      case "The Guessinator":
        return {
          question: question.question,
          correctAnswers: [],
          gameWord: question.gameWord || question.correctAnswers[0],
          score: question.score,
        };
      case "Flashcards":
        return {
          question: question.question,
          cards: question.cards || [],
        };
      case "Open-Ended Text":
        return {
          content: question.content,
          score: question.score,
          border: question.border,
          tip: question.tip,
          questionId: question.questionId,
          questionCategories: question.questionCategories || [],
        };
      default:
        return question;
    }
  };

  const handleAudioSave = async (audioBlob, questionId) => {
    if (!uid) {
      console.error("User ID is not available");
      alert("Unable to save audio. Please try logging in again.");
      return;
    }

    try {
      const audioFile = new File([audioBlob], `audio_${Date.now()}.wav`, {
        type: "audio/wav",
      });

      const storage = getStorage();
      const storageRef = ref(
        storage,
        `studentResponses/${uid}/${courseId}/${moduleId}/${questionId}_${Date.now()}.wav`
      );
      await uploadBytes(storageRef, audioFile);
      const audioURL = await getDownloadURL(storageRef);

      handleStudentResponse(questionId, {
        type: "AudioRecording",
        answer: audioURL,
        status: "Pending",
      });

      setReRecordingIndex(null);
    } catch (error) {
      console.error("Error saving audio:", error);
      alert("Failed to save audio. Please try again.");
    }
  };

  const handleSubmitAnswers = async () => {
    setIsSubmitAnswersLoading(true);
    try {
      const submitAnswersFunction = httpsCallable(
        functions,
        "submitStudentResponses"
      );

      // Filter out grade information, only send answers
      const responsesToSubmit = {};
      Object.entries(studentResponses).forEach(([questionId, response]) => {
        // Skip reserved module-level feedback key
        if (questionId === "__MODULE_OVERALL__") return;

        const { grade, ...answerOnly } = response;
        responsesToSubmit[questionId] = answerOnly;
      });

      await submitAnswersFunction({
        courseId,
        moduleId,
        sectionId,
        responses: responsesToSubmit,
      });
      setLastEditDate(new Date());
      setHasChangedSinceSubmission(false); // Reset flag after successful submission
      alert("Answers submitted successfully!");
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert(error.message || "Failed to submit answers. Please try again.");
    } finally {
      setIsSubmitAnswersLoading(false);
    }
  };

  const handleRetry = (index) => {
    setReRecordingIndex(index);
  };

  const handleAIFeedbackRequest = async (questionId) => {
    const question = moduleData.materials.find(
      (m) => m.questionId === questionId
    );

    if (!question) return;

    // For Open Text questions, handle differently
    if (question.questionType === "Open-Ended Text") {
      const formattedData = {
        questionType: question.questionType,
        content: question.content,
        additionalContext: {
          ...question,
        },
      };

      return new Promise((resolve) => {
        addToFeedbackQueue(
          questionId,
          question.questionType,
          formattedData,
          studentResponses[questionId]?.answer,
          (feedback) => {
            setAiFeedback((prev) => ({
              ...prev,
              [questionId]: feedback,
            }));
            resolve();
          }
        );
      });
    }

    // For other question types
    const formattedData = {
      questionType: question.questionType,
      question: question.question || "",
      options: question.options || [],
      correctAnswers:
        question.questionType === "Multiple Choice"
          ? [
              {
                index: question.correctAnswers[0],
                text: question.options[question.correctAnswers[0]],
              },
            ]
          : question.questionType === "Multiple Checkbox"
          ? (question.correctAnswers || []).map((index) => ({
              index: index,
              text: question.options[index],
            }))
          : question.correctAnswers || [],
      studentSelections:
        question.questionType === "Multiple Choice"
          ? {
              index: studentResponses[questionId]?.answer,
              text: question.options[studentResponses[questionId]?.answer],
            }
          : question.questionType === "Multiple Checkbox"
          ? (studentResponses[questionId]?.answer || []).map((index) => ({
              index: index,
              text: question.options[index],
            }))
          : studentResponses[questionId]?.answer || [],
      additionalContext: {
        ...question,
        studentResponse: studentResponses[questionId],
      },
    };

    // Special handling for different question types
    switch (question.questionType) {
      case "Sentence Ordering":
        formattedData.fullSentence = question.fullSentence;
        break;
      case "Spell the Word":
        formattedData.correctSpelling = question.sentence
          .match(/\[.*?\]/g)[0]
          .slice(1, -1);
        formattedData.sentence = question.sentence.replace(/\[.*?\]/g, "___");
        break;
      case "Pairing":
        formattedData.leftItems = question.leftItems || [];
        formattedData.rightItems = question.rightItems || [];
        formattedData.pairs = question.pairs || [];
        formattedData.studentSelections = Array.isArray(
          studentResponses[questionId]?.answer
        )
          ? studentResponses[questionId].answer
          : [];
        break;
    }

    return new Promise((resolve) => {
      addToFeedbackQueue(
        questionId,
        question.questionType,
        formattedData,
        formattedData.studentSelections,
        (feedback) => {
          setAiFeedback((prev) => ({
            ...prev,
            [questionId]: feedback,
          }));
          resolve();
        }
      );
    });
  };

  const handleCheckPracticeAnswers = async () => {
    if (moduleData.isQuizMode) {
      alert("Cannot check answers in quiz mode");
      return;
    }

    setIsCheckAnswersLoading(true);
    try {
      const checkPracticeAnswersFunction = httpsCallable(
        functions,
        "checkPracticeAnswers"
      );
      const result = await checkPracticeAnswersFunction({
        courseId,
        moduleId,
        studentResponses,
        questionData: moduleData.materials
          .filter((m) => m.type === "questions")
          .reduce((acc, q) => {
            acc[q.questionId] = {
              questionType: q.questionType,
              options: q.options,
              correctAnswers: q.correctAnswers,
            };
            return acc;
          }, {}),
      });
      setPracticeResults(result.data.results);
    } catch (error) {
      console.error("Error checking practice answers:", error);
      alert("Failed to check practice answers. Please try again.");
    } finally {
      setIsCheckAnswersLoading(false);
    }
  };

  const handleMaterialChange = (index, changes) => {
    setModuleData((prevData) => {
      const newMaterials = [...prevData.materials];
      newMaterials[index] = { ...newMaterials[index], ...changes };
      return {
        ...prevData,
        materials: newMaterials,
      };
    });
  };

  const handleExpiration = async () => {
    try {
      setIsSubmitAnswersLoading(true);
      const submitAnswersFunction = httpsCallable(
        functions,
        "submitStudentResponses"
      );

      // Filter out grade information, only send answers
      const responsesToSubmit = {};
      Object.entries(studentResponsesRef.current).forEach(
        ([questionId, response]) => {
          // Skip reserved module-level feedback key
          if (questionId === "__MODULE_OVERALL__") return;

          const { grade, ...answerOnly } = response;
          responsesToSubmit[questionId] = answerOnly;
        }
      );

      await submitAnswersFunction({
        courseId,
        moduleId,
        sectionId,
        responses: responsesToSubmit,
      });
      setLastEditDate(new Date());
      setHasChangedSinceSubmission(false); // Reset flag after auto-submission
      navigate("/my-courses");
    } catch (error) {
      console.error("Error auto-submitting answers:", error);
    } finally {
      setIsSubmitAnswersLoading(false);
    }
  };

  const handleModuleOverallAIFeedbackRequest = async (moduleOverallId) => {
    if (moduleOverallId !== "__MODULE_OVERALL__") return;

    try {
      await generateModuleSummaryFeedback(
        studentResponses,
        moduleData,
        setModuleOverallAiFeedback
      );
    } catch (error) {
      console.error("Error generating module summary feedback:", error);
      alert("Failed to generate module summary. Please try again.");
    }
  };

  const handleSaveFeedback = (questionId, feedback) => {
    // Only allow teachers to modify feedback
    if (!isTeacher) return;

    setStudentResponses((prev) => {
      const updated = { ...prev };
      if (!updated[questionId]) {
        updated[questionId] = {};
      }
      updated[questionId].grade = {
        ...updated[questionId]?.grade,
        feedback,
      };
      return updated;
    });

    // Track modified feedback
    setModifiedFeedback((prev) => ({
      ...prev,
      [questionId]: feedback,
    }));
  };

  const handleAutoEvaluate = async () => {
    setIsAutoEvaluating(true);
    try {
      const questions = moduleData.materials.filter(
        (m) => m.type === "questions"
      );

      for (const question of questions) {
        const studentResponse = studentResponses[question.questionId];
        if (studentResponse) {
          await handleAIFeedbackRequest(question.questionId);
        }
      }
    } catch (error) {
      console.error("Error in auto evaluation:", error);
      alert("Error during auto evaluation. Please try again.");
    } finally {
      setIsAutoEvaluating(false);
    }
  };

  const handleSaveAllFeedback = async () => {
    if (Object.keys(modifiedFeedback).length === 0) {
      alert("No feedback changes to save!");
      return;
    }

    setIsSubmitAnswersLoading(true);
    try {
      const saveAllFeedbackFunction = httpsCallable(
        functions,
        "saveAllTeacherFeedback"
      );

      // Only send modified feedback
      const feedbackToSave = {};
      Object.entries(modifiedFeedback).forEach(([questionId, feedback]) => {
        if (feedback === "__DELETE__") {
          feedbackToSave[questionId] = "__DELETE__";
        } else if (feedback) {
          feedbackToSave[questionId] = feedback;
        }
      });

      await saveAllFeedbackFunction({
        courseId,
        moduleId,
        studentId: userId || uid,
        feedbackData: feedbackToSave,
        userAvatar: avatar,
        graderName: `${firstName} ${lastName}`,
      });

      // Update local state only for modified feedback
      setStudentResponses((prev) => {
        const updated = { ...prev };
        Object.entries(feedbackToSave).forEach(([questionId, feedback]) => {
          if (feedback === "__DELETE__") {
            if (updated[questionId]) {
              delete updated[questionId].grade;
            }
          } else {
            if (!updated[questionId]) {
              updated[questionId] = {};
            }
            updated[questionId].grade = {
              ...updated[questionId]?.grade,
              feedback: feedback,
              gradedAt: new Date(),
              graderAvatar: avatar || "Avatar0",
              graderName: `${firstName} ${lastName}`,
            };
          }
        });
        return updated;
      });

      // Clear modified feedback tracking
      setModifiedFeedback({});
      alert("Feedback saved successfully!");
    } catch (error) {
      console.error("Error saving feedback:", error);
      alert("Failed to save feedback. Please try again.");
    } finally {
      setIsSubmitAnswersLoading(false);
    }
  };

  const getQuestionNumber = (currentIndex) => {
    let questionCount = 0;
    for (let i = 0; i <= currentIndex; i++) {
      if (isCountableQuestion(moduleData.materials[i])) {
        questionCount++;
      }
    }
    return questionCount;
  };

  // Add flip handler function for students
  const handleFlip = (materialIndex) => (e) => {
    e.stopPropagation();

    const element = materialRefs.current[materialIndex];
    if (element && !element.classList.contains("is-flipping")) {
      element.classList.add("is-flipping");

      setFlippedCards((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(materialIndex)) {
          newSet.delete(materialIndex);
        } else {
          newSet.add(materialIndex);
        }
        return newSet;
      });

      setTimeout(() => {
        element.classList.remove("is-flipping");
      }, 1200); // Match CSS transition duration
    }
  };

  // TipSide component for students - read-only tip display
  const StudentTipSide = ({ material, materialIndex }) => {
    return (
      <div className="card-face back">
        <div className={`question-container ${material.border || "shadow"}`}>
          <div className="question-header tip-back-header">
            {material.tip ? (
              <>
                <h3 className="tip-title">Teacher's Tip</h3>
                <div
                  className="tip-text"
                  dangerouslySetInnerHTML={{ __html: material.tip }}
                />
              </>
            ) : (
              <div className="tip-side-placeholder">
                Nothing is written on the back of this card.
              </div>
            )}
          </div>
          <div className="tip-return-button-container">
            <button
              className="tip-return-button"
              onClick={handleFlip(materialIndex)}
            >
              <FontAwesomeIcon icon={faRetweet} size="lg" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="student-module-view">
      <div className="status-bar">
        <span
          className={`status-field ${isTeacher ? "clickable-course-name" : ""}`}
          onClick={handleCourseClick}
        >
          Course Name: {courseName}
        </span>
        <span
          className={`status-field ${isTeacher ? "clickable-module-name" : ""}`}
          onClick={handleModuleClick}
        >
          Module Name: {moduleData?.name}
        </span>
        <span className="status-field">
          {lastEditDate
            ? `Answers submitted on: ${lastEditDate.toLocaleString()}`
            : isTeacher && !isViewingAsStudent
            ? "Viewing as Student"
            : "No submissions yet"}
          {lastEditDate &&
            ((!isViewingAsStudent && !isTeacher) || // Students can delete their own submissions
              (isTeacher && isViewingAsStudent)) && ( // Teachers can delete student submissions when viewing as student
              <button
                className="delete-submission-btn"
                onClick={handleDeleteSubmission}
                disabled={isDeleteLoading}
                style={{
                  marginLeft: "10px",
                  padding: "1px 5px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: isDeleteLoading ? "not-allowed" : "pointer",
                  opacity: isDeleteLoading ? 0.6 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ marginRight: "4px" }}
                />
                {isDeleteLoading ? "Deleting..." : "Delete Submission"}
              </button>
            )}
        </span>
        {isTeacher && !isViewingAsStudent && !lastEditDate && (
          <span
            className="status-field clickable-course-name"
            onClick={handleGoBackToModule}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faEye} style={{ marginRight: "4px" }} />
            Go back to Module
          </span>
        )}
      </div>
      <div className="materials">
        {moduleData.materials.map((material, index) => (
          <div
            key={material.id || index}
            ref={(el) => (materialRefs.current[index] = el)}
            className={`material-item ${
              material.type === "video" && material.youtubeLink
                ? "original"
                : material.size
            } ${material.border} ${material.type} ${
              flippedCards.has(index) ? "flipped" : ""
            }`}
          >
            {/* For question types, wrap in card flip structure */}
            {material.type === "questions" ? (
              <>
                {/* Front face of the card */}
                <div className="card-face front">
                  {isCountableQuestion(material) && (
                    <span
                      className={`question-number ${
                        getQuestionNumber(index) >= 10 ? "double-digit" : ""
                      }`}
                    >
                      {getQuestionNumber(index)}
                    </span>
                  )}
                  {material.questionType === "Multiple Choice" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <MultipleChoiceQuestion
                        question={material.question}
                        options={material.options}
                        correctAnswers={[]}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, response)
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        materialCategories={{}}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Multiple Checkbox" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <MultipleCheckboxQuestion
                        question={material.question}
                        options={material.options}
                        correctAnswers={[]}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, response)
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        questionCategories={material.questionCategories || []}
                        questionId={material.questionId}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Fill in the Blanks" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <FillInTheBlanks
                        question={material.question}
                        correctAnswers={[]}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "FillInTheBlanks",
                            answer: response.answer,
                          })
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        materialCategories={{}}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Spell the Word" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <SpellTheWord
                        sentence={material.sentence}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "SpellTheWord",
                            answer: response.answer,
                          })
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        materialCategories={{}}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Sentence Ordering" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <SentenceOrdering
                        sentences={material.sentences || []}
                        correctAnswers={[]}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "SentenceOrdering",
                            answer: response.answer,
                          })
                        }
                        border={material.border}
                        score={material.score}
                        questionId={material.questionId}
                        question={material.question || ""}
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        materialCategories={{}}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Pairing" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <Pairing
                        question={material.question}
                        leftItems={material.leftItems}
                        rightItems={material.rightItems}
                        pairs={[]}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        questionCategories={material.questionCategories || []}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "Pairing",
                            answer: response.studentPairs,
                          })
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        materialCategories={{}}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "The Guessinator" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <TheGuessinator
                        question={material.question}
                        gameWord={material.gameWord}
                        tip={material.tip}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "TheGuessinator",
                            answer: response.guessedLetters,
                            gameStatus: response.gameStatus,
                            remainingAttempts: response.remainingAttempts,
                          })
                        }
                        border={material.border}
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Open-Ended Text" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <OpenTextQuestion
                        content={material.content}
                        score={material.score}
                        border={material.border}
                        tip={material.tip}
                        questionId={material.questionId}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, response)
                        }
                        isStudentView={true}
                        studentResponse={studentResponses[material.questionId]}
                        questionCategories={material.questionCategories || []}
                        onFlip={handleFlip(index)}
                      />
                    </QuestionWrapper>
                  )}
                  {material.questionType === "Flashcards" && (
                    <QuestionWrapper
                      material={material}
                      moduleData={moduleData}
                      aiFeedback={aiFeedback}
                      practiceResults={practiceResults}
                      onAIFeedbackRequest={handleAIFeedbackRequest}
                      isTeacher={isTeacher}
                      isViewingAsStudent={isViewingAsStudent}
                      studentResponses={studentResponses}
                      onSaveFeedback={handleSaveFeedback}
                      editingFeedback={editingFeedback}
                      onEditFeedback={setEditingFeedback}
                    >
                      <Flashcards
                        question={material.question}
                        cards={material.cards || []}
                        onChange={(response) =>
                          handleStudentResponse(material.questionId, {
                            type: "Flashcards",
                            answer: response.cards,
                          })
                        }
                        isStudentView={true}
                        border={material.border}
                        tip={material.tip}
                      />
                    </QuestionWrapper>
                  )}
                </div>

                {/* Back face of the card - only for questions except Flashcards */}
                {material.questionType !== "Flashcards" && (
                  <StudentTipSide material={material} materialIndex={index} />
                )}
              </>
            ) : (
              /* Non-question materials remain unchanged */
              <>
                {isCountableQuestion(material) && (
                  <span
                    className={`question-number ${
                      getQuestionNumber(index) >= 10 ? "double-digit" : ""
                    }`}
                  >
                    {getQuestionNumber(index)}
                  </span>
                )}
                {material.type === "image" && (
                  <div>
                    <img
                      src={material.imageURL}
                      alt={material.caption || "Module image"}
                    />
                    {material.caption && <p>{material.caption}</p>}
                  </div>
                )}
                {material.type === "video" && (
                  <div>
                    {material.youtubeLink ? (
                      <div className="iframe-wrapper youtube-wrapper">
                        <div id={`youtube-player-${index}`}></div>
                      </div>
                    ) : (
                      <video controls src={material.videoURL}>
                        Your browser does not support the video tag.
                      </video>
                    )}
                    {material.caption && <p>{material.caption}</p>}
                  </div>
                )}
                {material.type === "audio" && (
                  <div>
                    <AudioPlayer
                      audioURL={material.audioURL}
                      question={material.question}
                      score={material.score}
                      isStudentView={true}
                      onChange={() => {}}
                      border={material.border}
                    />
                    {material.caption && <p>{material.caption}</p>}
                  </div>
                )}
                {material.type === "audio-recorder" && (
                  <AudioRecorderWrapper
                    index={index}
                    material={material}
                    reRecordingIndex={reRecordingIndex}
                    studentResponses={studentResponses}
                    handleAudioSave={handleAudioSave}
                    handleRetry={handleRetry}
                    handleMaterialChange={handleMaterialChange}
                    isTeacher={isTeacher}
                    isViewingAsStudent={isViewingAsStudent}
                    aiFeedback={aiFeedback}
                    editingFeedback={editingFeedback}
                    setEditingFeedback={setEditingFeedback}
                    handleSaveFeedback={handleSaveFeedback}
                    handleAIFeedbackRequest={handleAIFeedbackRequest}
                    moduleData={moduleData}
                  />
                )}
                {material.type === "text" && (
                  <div
                    className={`question-container ${
                      material.border || "shadow"
                    }`}
                  >
                    <div className="question-header">
                      <div
                        className="text-content"
                        dangerouslySetInnerHTML={{ __html: material.content }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Module Overall Feedback */}
      {((isTeacher && isViewingAsStudent) || !isTeacher) && (
        <ModuleOverallFeedback
          feedback={studentResponses.__MODULE_OVERALL__?.grade?.feedback}
          onSave={handleSaveFeedback}
          isEditing={editingFeedback === "__MODULE_OVERALL__"}
          onEditClick={setEditingFeedback}
          onAIFeedbackRequest={handleModuleOverallAIFeedbackRequest}
          aiFeedback={moduleOverallAiFeedback}
          isTeacher={isTeacher}
          studentResponses={studentResponses}
        />
      )}

      {(isViewingAsStudent || !isTeacher) && (
        <div className="button-container">
          <div className="button-controls">
            {isTeacher ? (
              <>
                {[
                  "teacherplus",
                  "admin",
                  "technician",
                  "technicianplus",
                ].includes(role) && (
                  <button
                    onClick={handleAutoEvaluate}
                    className="auto-evaluate-button"
                    disabled={isAutoEvaluating}
                  >
                    <FontAwesomeIcon
                      icon={faRobot}
                      className="ai-icon"
                      spin={isAutoEvaluating}
                    />
                    {isAutoEvaluating ? "Evaluating..." : "Auto Evaluate All"}
                  </button>
                )}
                <button
                  onClick={handleSaveAllFeedback}
                  className="save-feedback-button"
                  disabled={isSubmitAnswersLoading}
                >
                  <FontAwesomeIcon
                    icon={faSave}
                    className="save-icon"
                    spin={isSubmitAnswersLoading}
                  />
                  Save Feedback
                </button>
              </>
            ) : (
              <>
                {!moduleData.isQuizMode && (
                  <button
                    onClick={handleCheckPracticeAnswers}
                    className="check-practice-answers-button"
                    disabled={isCheckAnswersLoading}
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="button-icon check-icon"
                      spin={isCheckAnswersLoading}
                    />
                    Check Answers
                  </button>
                )}
                <button
                  onClick={handleSubmitAnswers}
                  className="submit-answers-button"
                  disabled={isSubmitAnswersLoading}
                >
                  <div className="button-text">
                    <FontAwesomeIcon
                      icon={faPaperPlane}
                      className="button-icon submit-icon"
                      spin={isSubmitAnswersLoading}
                    />
                    Submit Answers
                  </div>
                </button>
                {sectionAvailability && (
                  <div className="countdown-container">
                    <AvailabilityCountdown
                      startDate={sectionAvailability.startDate}
                      endDate={sectionAvailability.endDate}
                      userOverrides={sectionAvailability.userOverrides}
                      onExpire={handleExpiration}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentModuleView;
